/**
 * Send Visibility — Overview widgets for daily-send tracking.
 * ────────────────────────────────────────────────────────────
 * Per SEND_VISIBILITY_SPEC.md, three additions to Crew Chief Overview:
 *   SendTile          — replaces the stale "Emails Sent" tile with a live one
 *   DailySendChart    — 30-day stacked bar chart, SourcePoint vs. others
 *   SourcePointPanel  — leads-in-queue + sent + replies + MRR + manual push
 *
 * Single source of truth: GET /api/admin/outreach-stats
 * Sent-event definition (all three widgets): distinct arclight_leads.pitch_sent_at
 * The tile / chart / panel MUST reconcile — same query powers all three.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { Megaphone, TrendingUp, TrendingDown, ArrowRight, Flame, Send, ExternalLink, Loader2, AlertCircle } from 'lucide-react';

const API    = process.env.REACT_APP_API_URL || 'https://aria-api-ibja.onrender.com';
const APIKEY = process.env.REACT_APP_API_KEY || '';
const SOURCEPOINT_SLUG = 'sourcepoint_website_mrr';

// Match backend env defaults so annotations stay in sync
const DAILY_SEND_TARGET   = Number(process.env.REACT_APP_DAILY_SEND_TARGET   || 500);
const INSTANTLY_HARD_CAP  = Number(process.env.REACT_APP_INSTANTLY_HARD_CAP  || 2000);

async function apiGet(path) {
  const headers = APIKEY ? { 'X-ARIA-API-Key': APIKEY, 'X-API-Key': APIKEY } : {};
  try {
    const r = await fetch(`${API}${path}`, { headers });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return { data: await r.json(), error: null };
  } catch (e) {
    return { data: null, error: e.message };
  }
}
async function apiPost(path) {
  const headers = APIKEY
    ? { 'X-ARIA-API-Key': APIKEY, 'X-API-Key': APIKEY, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
  try {
    const r = await fetch(`${API}${path}`, { method: 'POST', headers });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return { data: await r.json(), error: null };
  } catch (e) {
    return { data: null, error: e.message };
  }
}

const nFmt = (n) => (n == null ? '—' : Number(n).toLocaleString());
const money = (n) => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Number(n).toFixed(0)}`;
};

// ═══════════════════════════════════════════════════════════════════════════
// SendTile — replaces the stale "Emails Sent" KpiCard
// ═══════════════════════════════════════════════════════════════════════════
export function SendTile() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    // 30 days covers today + last 7d + last 30d + yesterday in one call
    const { data } = await apiGet('/api/admin/outreach-stats?days=30');
    if (data) setStats(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  const numbers = useMemo(() => {
    if (!stats?.days?.length) return null;
    const days = stats.days;
    const today = days[days.length - 1]?.emailed || 0;
    const yesterday = days[days.length - 2]?.emailed || 0;
    const last7 = days.slice(-7).reduce((s, d) => s + (d.emailed || 0), 0);
    const last30 = days.reduce((s, d) => s + (d.emailed || 0), 0);
    let trend = 'flat';
    if (today > yesterday * 1.05) trend = 'up';
    else if (today < yesterday * 0.95) trend = 'down';
    return { today, yesterday, last7, last30, trend };
  }, [stats]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-start justify-between">
        <div className="p-2.5 rounded-xl bg-sky-50 text-sky-600"><Megaphone className="w-5 h-5" /></div>
        {numbers && (
          <div className={`text-xs font-medium flex items-center gap-0.5 ${
            numbers.trend === 'up' ? 'text-emerald-600' :
            numbers.trend === 'down' ? 'text-red-600' : 'text-slate-400'
          }`}>
            {numbers.trend === 'up' && <TrendingUp className="w-3 h-3" />}
            {numbers.trend === 'down' && <TrendingDown className="w-3 h-3" />}
            {numbers.trend === 'flat' && <ArrowRight className="w-3 h-3" />}
            vs. yesterday ({nFmt(numbers.yesterday)})
          </div>
        )}
      </div>
      {loading && !numbers ? (
        <div className="mt-3"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
      ) : (
        <>
          <p className="mt-3 text-2xl font-bold text-slate-900 leading-none">
            {numbers ? nFmt(numbers.today) : '0'}
          </p>
          <p className="text-xs text-slate-500 mt-1.5">Sent today</p>
          {numbers && (
            <p className="text-xs text-slate-400 mt-0.5">
              7d: {nFmt(numbers.last7)} · 30d: {nFmt(numbers.last30)}
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DailySendChart — 30-day stacked bar, SourcePoint primary vs. others gray
// ═══════════════════════════════════════════════════════════════════════════
export function DailySendChart() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await apiGet('/api/admin/outreach-stats?days=30&breakdown=vertical');
    if (data) setStats(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  // Transform: { date, sourcepoint, other } per day for the stacked bar
  const chartData = useMemo(() => {
    if (!stats?.days) return [];
    return stats.days.map((d) => {
      const bv = d.by_vertical || {};
      const sourcepoint = bv[SOURCEPOINT_SLUG] || 0;
      const total = d.emailed || 0;
      return {
        date: d.date,
        label: d.date.slice(5), // MM-DD
        sourcepoint,
        other: Math.max(0, total - sourcepoint),
        total,
      };
    });
  }, [stats]);

  if (loading && chartData.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
      </div>
    );
  }

  const todayTotal = chartData[chartData.length - 1]?.total || 0;
  const todaySourcePoint = chartData[chartData.length - 1]?.sourcepoint || 0;
  const emptyToday = todayTotal === 0;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Daily Send · Last 30 Days</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            SourcePoint website offer highlighted · target line at {nFmt(DAILY_SEND_TARGET)}/day
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-indigo-500" /> SourcePoint</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-slate-300" /> Other</span>
        </div>
      </div>
      <div className="p-5">
        {emptyToday && (
          <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-center gap-2">
            <AlertCircle className="w-3.5 h-3.5" />
            No sends yet today. System will push at next campaign_push interval (every 4h).
          </div>
        )}
        <div className="w-full h-64">
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <XAxis dataKey="label" stroke="#94a3b8" fontSize={10} interval="preserveStartEnd" />
              <YAxis stroke="#94a3b8" fontSize={11} />
              <Tooltip
                formatter={(v, name) => [nFmt(v), name === 'sourcepoint' ? 'SourcePoint' : 'Other']}
                labelFormatter={(l, payload) => {
                  const p = payload?.[0]?.payload;
                  if (!p) return l;
                  const total = (p.sourcepoint || 0) + (p.other || 0);
                  const pct = total > 0 ? Math.round((p.sourcepoint / total) * 100) : 0;
                  return `${p.date} — total ${nFmt(total)} · SP ${pct}%`;
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine y={DAILY_SEND_TARGET}  stroke="#10b981" strokeDasharray="4 4" label={{ value: `Target ${DAILY_SEND_TARGET}`, fontSize: 10, fill: '#10b981', position: 'insideTopRight' }} />
              <ReferenceLine y={INSTANTLY_HARD_CAP} stroke="#ef4444" strokeDasharray="4 4" label={{ value: `Cap ${INSTANTLY_HARD_CAP}`, fontSize: 10, fill: '#ef4444', position: 'insideTopRight' }} />
              <Bar dataKey="sourcepoint" stackId="a" name="SourcePoint" fill="#6366f1" />
              <Bar dataKey="other"       stackId="a" name="Other"       fill="#cbd5e1" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
          <span>Today: <span className="font-semibold text-slate-900">{nFmt(todayTotal)}</span> total · <span className="font-semibold text-indigo-600">{nFmt(todaySourcePoint)}</span> SourcePoint</span>
          <span>30-day total: {nFmt(chartData.reduce((s, d) => s + d.total, 0))}</span>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SourcePointPanel — prioritization card with manual-push button
// ═══════════════════════════════════════════════════════════════════════════
export function SourcePointPanel({ onToast }) {
  const [stats, setStats] = useState(null);
  const [mrr, setMrr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pushing, setPushing] = useState(false);

  const load = useCallback(async () => {
    const [s, m] = await Promise.all([
      apiGet(`/api/admin/vertical-stats/${SOURCEPOINT_SLUG}`),
      apiGet(`/api/accounts/mrr-history?months=2&offer_slug=${SOURCEPOINT_SLUG}`),
    ]);
    if (s.data) setStats(s.data);
    if (m.data) setMrr(m.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  const triggerPush = async () => {
    setPushing(true);
    const { data, error } = await apiPost('/api/scheduler/run/campaign_push');
    setPushing(false);
    if (error) { onToast && onToast('Push failed', error, 'error'); return; }
    onToast && onToast('Campaign push triggered', 'Refreshing in ~60s…', 'success');
    setTimeout(load, 60_000);
  };

  const currentMrr = mrr?.months?.[mrr.months.length - 1]?.mrr_run_rate || 0;
  const newMrrThisMonth = mrr?.months?.[mrr.months.length - 1]?.new_mrr || 0;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
      <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Flame className="w-4 h-4 text-indigo-500" />
            Website Offer — SourcePoint
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">$297/mo · {SOURCEPOINT_SLUG}</p>
        </div>
        {stats?.instantly_campaign_id && (
          <a
            href={`https://app.instantly.ai/app/campaigns/${stats.instantly_campaign_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-slate-400 hover:text-indigo-600 inline-flex items-center gap-1"
            title="Open Instantly campaign"
          >
            Instantly <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
      {loading ? (
        <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
      ) : (
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Metric label="Leads in queue"      value={nFmt(stats?.leads_in_queue)} hint="ready to send" />
            <Metric label="Sent today"          value={nFmt(stats?.sent_today)} />
            <Metric label="Sent this week"      value={nFmt(stats?.sent_this_week)} />
            <Metric label="Replies (30d)"       value={nFmt(stats?.replies_30d)} />
            <Metric label="Positive replies"    value={nFmt(stats?.positive_replies_30d)} highlight />
            <Metric label="MRR run-rate"        value={money(currentMrr)} highlight hint={newMrrThisMonth > 0 ? `+${money(newMrrThisMonth)} new` : null} />
          </div>
          <button
            onClick={triggerPush}
            disabled={pushing}
            className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg py-2.5 text-sm font-medium"
          >
            {pushing ? <><Loader2 className="w-4 h-4 animate-spin" /> Pushing…</> : <><Send className="w-4 h-4" /> Trigger website-offer push now</>}
          </button>
          {!stats?.is_active && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Vertical is not currently active. Manual push will still fire but scheduled sends are paused.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, hint, highlight }) {
  return (
    <div className={`rounded-lg px-3 py-2.5 ${highlight ? 'bg-indigo-50 border border-indigo-100' : 'bg-slate-50'}`}>
      <div className={`text-lg font-bold ${highlight ? 'text-indigo-700' : 'text-slate-900'}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
      {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}
